import { queryOptions } from '@tanstack/react-query';

import { AuthError, authClient, authErrorCode, toAuthError } from './auth';

/**
 * The signed-in staff member's own account, on the one store that holds it: the auth
 * Worker's user (`settings-is-a-dialog` D2). Settings is the only caller.
 *
 * MathGaps/schools does all of this already for a teacher
 * (`apps/web/src/lib/auth/profile.ts`); A5 forbids importing it, so the pattern is
 * written here. What differs is recorded where it differs: the sign-out switch on a
 * password change (D6) and the one name field (D3).
 *
 * Every write ends by leaving the caller to settle the caches (D12) — this module makes
 * no React call and holds no state, so `useAccountWrite` does the invalidating.
 */

/** What the dialog draws, as the Worker answers `/get-session`. */
export interface Account {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  /** Whether a second factor is owed at sign-in, either method. */
  twoFactorEnabled: boolean;
  /** The account's number, E.164, or null. Present but unproved is still null to the dialog. */
  phoneNumber: string | null;
  phoneNumberVerified: boolean;
}

/** Everything this surface caches. Separate from the shell's `['session']`, which has its own life (D2). */
export const ACCOUNT_KEY = ['account'] as const;

/** The user the Worker answered with, as the two plugins extend it. */
interface SessionUser {
  id: string;
  name: string;
  email: string;
  emailVerified?: boolean | null;
  twoFactorEnabled?: boolean | null;
  phoneNumber?: string | null;
  phoneNumberVerified?: boolean | null;
}

/** The account, read from the Worker rather than from the session the shell cached at boot (D2). */
export async function readAccount(): Promise<Account> {
  const { data, error } = await authClient.getSession();
  if (error) throw toAuthError(error);
  const user = data?.user as SessionUser | undefined;
  if (!user) {
    throw new AuthError(
      'requires-recent-login',
      'You are not signed in any more. Sign in again to change your account.',
    );
  }
  return {
    id: user.id,
    name: user.name,
    email: user.email,
    emailVerified: user.emailVerified === true,
    twoFactorEnabled: user.twoFactorEnabled === true,
    /* Unproved is nothing to draw: the Worker texts to a proved number alone (`text-factor.ts`). */
    phoneNumber:
      user.phoneNumberVerified === true ? (user.phoneNumber ?? null) : null,
    phoneNumberVerified: user.phoneNumberVerified === true,
  };
}

export const accountQuery = queryOptions({
  queryKey: ACCOUNT_KEY,
  queryFn: readAccount,
});

/** A refusal whose only possible cause is the password field: its own sentence, not the sign-in form's (D9). */
function asWrongPassword(err: unknown): AuthError {
  const code = authErrorCode(err);
  if (code === 'INVALID_PASSWORD') {
    return new AuthError('wrong-password', 'Your password is wrong.', err);
  }
  return toAuthError(err);
}

/** The account's name. One field, because the account holds one name (D3). */
export async function updateAccountName(name: string): Promise<void> {
  const trimmed = name.trim();
  if (trimmed === '') {
    throw new AuthError('unknown', 'Enter a name.');
  }
  try {
    const { error } = await authClient.updateUser({ name: trimmed });
    if (error) throw error;
  } catch (err) {
    throw toAuthError(err);
  }
}

export interface EmailChange {
  newEmail: string;
  /** The Worker will not move an address without it (`services/auth/src/change-email.ts`). */
  password: string;
}

/**
 * Ask for the sign-in address to move (D4, D5).
 *
 * A raw `$fetch`, not `authClient.changeEmail`: Better Auth 1.7.5's `/change-email` body is
 * `{ newEmail, callbackURL }` and the Worker's own hook refuses the call without a `password`.
 * The Worker then mails the NEW address; the sign-in address moves when that link is followed,
 * so the caller says a mail was sent and never that the address changed.
 *
 * An address already on another account answers exactly as a free one and gets no mail: the
 * Worker never says which addresses exist, and nor does this.
 */
export async function requestEmailChange({
  newEmail,
  password,
}: EmailChange): Promise<void> {
  try {
    const { error } = await authClient.$fetch('/change-email', {
      method: 'POST',
      body: { newEmail, password, callbackURL: '/' },
    });
    if (error) throw error;
  } catch (err) {
    if (authErrorCode(err) === 'NO_PASSWORD') {
      throw new AuthError(
        'operation-not-allowed',
        'This account has no password, so there is no address to change here.',
        err,
      );
    }
    throw asWrongPassword(err);
  }
}

/** Mail the verification again, for the address the account has now (D10). */
export async function resendVerificationEmail(email: string): Promise<void> {
  try {
    const { error } = await authClient.sendVerificationEmail({
      email,
      callbackURL: '/',
    });
    if (error) throw error;
  } catch (err) {
    throw toAuthError(err);
  }
}

export interface PasswordChange {
  currentPassword: string;
  newPassword: string;
  /** The Worker's `revokeSessionsOnPasswordReset` governs a RESET, not a change: a change must ask (D6). */
  signOutOthers: boolean;
}

/** Replace the password with the current one in hand. The caller's own session survives either way. */
export async function updateAccountPassword({
  currentPassword,
  newPassword,
  signOutOthers,
}: PasswordChange): Promise<void> {
  try {
    const { error } = await authClient.changePassword({
      currentPassword,
      newPassword,
      revokeOtherSessions: signOutOthers,
    });
    if (error) throw error;
  } catch (err) {
    if (authErrorCode(err) === 'CREDENTIAL_ACCOUNT_NOT_FOUND') {
      throw new AuthError(
        'operation-not-allowed',
        'This account has no password, so there is no password to change.',
        err,
      );
    }
    throw asWrongPassword(err);
  }
}

/** What enrolling an authenticator hands the staff member to scan and to keep (D8). */
export interface AuthenticatorEnrolment {
  /** `otpauth://totp/Tutero:<email>?secret=…`. Shown, never logged, never persisted. */
  totpURI: string;
  /** One-time codes for a lost authenticator; the Worker issues them once. */
  backupCodes: readonly string[];
}

/**
 * Begin enrolling an authenticator. Nothing is owed at sign-in until `confirmAuthenticator`
 * has taken a code from the app, which is what proves the secret reached it.
 */
export async function enrolAuthenticator(
  password: string,
): Promise<AuthenticatorEnrolment> {
  try {
    const { data, error } = await authClient.twoFactor.enable({
      password,
      issuer: 'Tutero',
    });
    if (error) throw error;
    if (!data || data.method !== 'totp') {
      throw new AuthError(
        'unknown',
        'The Worker gave no authenticator secret.',
      );
    }
    return { totpURI: data.totpURI, backupCodes: data.backupCodes };
  } catch (err) {
    throw asWrongPassword(err);
  }
}

/** Finish the enrolment with the first code the authenticator shows. */
export async function confirmAuthenticator(code: string): Promise<void> {
  try {
    const { error } = await authClient.twoFactor.verifyTotp({ code });
    if (error) throw error;
  } catch (err) {
    throw toAuthError(err);
  }
}

/** Turn 2FA off, both methods, with the password (D7). */
export async function disableSecondFactor(password: string): Promise<void> {
  try {
    const { error } = await authClient.twoFactor.disable({ password });
    if (error) throw error;
  } catch (err) {
    throw asWrongPassword(err);
  }
}

/**
 * A number as the Worker takes it (E.164: a `+`, a leading digit other than zero, and
 * eight to fifteen digits in all), from what a staff member types — spaces, dashes,
 * brackets and dots dropped — or null when it is not one (D14).
 *
 * One field holding the whole number, rather than a country-code picker beside a
 * digits-only field, which is the pair that put two plus signs on every Flutter enrolment.
 */
export function phoneNumberOf(typed: string): string | null {
  const number = typed.replace(/[\s().-]/g, '');
  return /^\+[1-9]\d{7,14}$/.test(number) ? number : null;
}

/** Text a code to a number the staff member says is theirs. Nothing is stored until it is verified. */
export async function sendPhoneCode(phoneNumber: string): Promise<void> {
  try {
    const { error } = await authClient.phoneNumber.sendOtp({ phoneNumber });
    if (error) throw error;
  } catch (err) {
    throw toAuthError(err);
  }
}

/**
 * The texted code proves the number, and it becomes the account's. `updatePhoneNumber`
 * is not optional: without it the plugin signs in whoever holds the number, which is why
 * the Worker's hook refuses the call that omits it (`text-factor.ts`).
 */
export async function verifyPhoneNumber(
  phoneNumber: string,
  code: string,
): Promise<void> {
  try {
    const { error } = await authClient.phoneNumber.verify({
      phoneNumber,
      code,
      updatePhoneNumber: true,
    });
    if (error) throw error;
  } catch (err) {
    if (authErrorCode(err) === 'PHONE_NUMBER_EXIST') {
      throw new AuthError(
        'operation-not-allowed',
        'That number is already on a Tutero account.',
        err,
      );
    }
    throw toAuthError(err);
  }
}

/** Make a texted code the second step. On at once: the number is already proved (D15). */
export async function enrolTextFactor(password: string): Promise<void> {
  try {
    const { error } = await authClient.twoFactor.enable({
      password,
      method: 'otp',
    });
    if (error) throw error;
  } catch (err) {
    throw asWrongPassword(err);
  }
}

/**
 * Take the number off the account. Refused in the Worker's own words while a texted code
 * is the only second step (`TEXT_IS_THE_SECOND_STEP`), which `toAuthError` passes through.
 */
export async function removePhoneNumber(): Promise<void> {
  try {
    const { error } = await authClient.updateUser({ phoneNumber: null });
    if (error) throw error;
  } catch (err) {
    throw toAuthError(err);
  }
}
